
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Building, CheckCircle, AlertCircle } from "lucide-react";


const formatearFecha = (fecha) => {
  if (!fecha) return "-";
  try {
    return new Date(fecha).toLocaleDateString("es-ES");
  } catch (e) {
    return fecha;
  }
};

const formatearMoneda = (cantidad) => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(cantidad);
};

export default function ResumenUsuario() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usuario, setUsuario] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [res, userRes] = await Promise.all([
          axios.get(`/api/pagos/usuario/${id}/resumen`),
          axios.get(`/api/users`),
        ]);
        setResumen(res.data);
        const found = userRes.data.find((u) => u.id === parseInt(id));
        setUsuario(found);
      } catch (e) {
        setResumen(null);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  if (loading) return <div className="loading">Cargando...</div>;
  if (!resumen || !usuario) return <div>Error al cargar el resumen.</div>;

  // Calcular total pendiente
  const totalPendiente = resumen.pendientes.reduce((acc, g) => acc + Number(g.importe_total || 0), 0);


  // Badge visual para tipo de gasto (igual que Gastos)
  const tipoBadge = (tipo) => {
    const map = {
      mensualidad: { label: "Mensualidad", className: "badge badge-secondary" },
      agua: { label: "Agua", className: "badge badge-secondary" },
      luz: { label: "Electricidad", className: "badge badge-secondary" },
      otros: { label: "Otros", className: "badge badge-secondary" },
    };
    const t = map[tipo] || { label: tipo, className: "badge badge-secondary" };
    return <span className={t.className}>{t.label}</span>;
  };

  // Badge visual para estado de pago
  const estadoBadge = (estado) => {
    if (estado === "pagado") {
      return (
        <span className="badge badge-success">
          <CheckCircle size={12} style={{marginRight:4}} />Pagado
        </span>
      );
    }
    return (
      <span className="badge badge-warning">
        <AlertCircle size={12} style={{marginRight:4}} />Pendiente
      </span>
    );
  };

  return (
    <div className="container">
      <button className="btn btn-link" onClick={() => navigate(-1)}>
        ← Volver
      </button>
      <h1>Resumen de {usuario.nombre} {usuario.apellidos}</h1>

      <div style={{
        background: "#f1f5f9",
        borderRadius: 10,
        padding: 18,
        margin: "18px 0 24px 0",
        display: "flex",
        alignItems: "center",
        gap: 24,
        boxShadow: "0 1px 4px #0001"
      }}>
        <span style={{fontWeight:600, fontSize:18}}>Total pendiente:</span>
        <span style={{fontWeight:700, fontSize:22, color: totalPendiente > 0 ? '#dc2626' : '#16a34a'}}>
          {totalPendiente.toFixed(2)} €
        </span>
      </div>


      <h2>Pendiente de pago</h2>
      {resumen.pendientes.length === 0 ? (
        <p>No hay deudas pendientes.</p>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th>Tipo</th>
                  <th>Habitación</th>
                  <th>Importe</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {resumen.pendientes.map((g) => (
                  <tr key={g.gasto_id}>
                    <td>{g.concepto}</td>
                    <td>{tipoBadge(g.tipo_gasto)}</td>
                    <td>
                      <span style={{display:'flex',alignItems:'center',gap:6}}>
                        <Building size={16} />
                        {g.habitacion_numero || '-'}
                      </span>
                    </td>
                    <td style={{fontWeight:600}}>{formatearMoneda(g.importe_total || 0)}</td>
                    <td>{estadoBadge("pendiente")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h2 style={{marginTop:32}}>Pagado</h2>
      {resumen.pagados.length === 0 ? (
        <p>No hay pagos registrados.</p>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th>Tipo</th>
                  <th>Habitación</th>
                  <th>Importe</th>
                  <th>Estado</th>
                  <th>Fecha Pago</th>
                  <th>Método</th>
                  <th>Notas</th>
                </tr>
              </thead>
              <tbody>
                {resumen.pagados.map((g) => (
                  <tr key={g.gasto_id}>
                    <td>{g.concepto}</td>
                    <td>{tipoBadge(g.tipo_gasto)}</td>
                    <td>
                      <span style={{display:'flex',alignItems:'center',gap:6}}>
                        <Building size={16} />
                        {g.habitacion_numero || '-'}
                      </span>
                    </td>
                    <td style={{fontWeight:600}}>{formatearMoneda(g.importe_total || 0)}</td>
                    <td>{estadoBadge("pagado")}</td>
                    <td>{formatearFecha(g.fecha_pago)}</td>
                    <td>{g.metodo_pago || '-'}</td>
                    <td>{g.notas || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
